<!-- src/app/settings/accruals/pages/employee-leave-balances/employee-leave-balances.component.html -->
<accruals-nav></accruals-nav>

<div class="employee-leave-balances-container">
  <!-- Loading -->
  <div *ngIf="isLoading" class="spinner-container">
    <div class="spinner"></div>
  </div>

  <!-- Error -->
  <div *ngIf="errorMessage && !isLoading" class="error-message">
    {{ errorMessage }}
  </div>

  <ng-container *ngIf="!isLoading">
    <!-- Header -->
    <div class="header">
      <h2>Employee Leave Balances</h2>
    </div>

    <!-- Filters -->
    <div class="filters-container">
      <div class="filters-row">
        <label class="filter-field search-field">
          <span>Search</span>
          <input
            type="text"
            [(ngModel)]="searchInputValue"
            (ngModelChange)="onSearchTermChange($event)"
            placeholder="Search employee, number, company, or department"
          />
        </label>

        <label class="filter-field">
          <span>Company</span>
          <select
            [ngModel]="filters.companyId"
            (ngModelChange)="onCompanyChange($event)"
          >
            <option [ngValue]="null">All companies</option>
            <option *ngFor="let company of userFilterValues?.companies" [ngValue]="company.id">
              {{ company.name }}
            </option>
          </select>
        </label>

        <label class="filter-field">
          <span>Department</span>
          <select
            [ngModel]="filters.departmentId"
            (ngModelChange)="onDepartmentChange($event)"
          >
            <option [ngValue]="null">All departments</option>
            <option *ngFor="let dept of userFilterValues?.departments" [ngValue]="dept.id">
              {{ dept.code }} - {{ dept.name }}
            </option>
          </select>
        </label>

        <div class="filter-actions">
          <button
            class="clear-filters-btn"
            type="button"
            (click)="clearFilters()"
            [disabled]="!hasActiveFilters"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Sticky Pagination -->
    <div class="pagination sticky">
      <div class="page-controls">
        <button (click)="changePage(currentPage - 1)" [disabled]="currentPage === 1">
          Prev
        </button>

        <span>Page {{ currentPage }} of {{ totalPages }}</span>

        <button (click)="changePage(currentPage + 1)" [disabled]="currentPage === totalPages">
          Next
        </button>
      </div>

      <div class="page-size">
        <label for="pageSize">Page Size:</label>
        <select id="pageSize" [(ngModel)]="pageSize" (change)="changePageSize(pageSize)">
          <option *ngFor="let size of pageSizes" [value]="size">{{ size }}</option>
        </select>
      </div>
    </div>

    <!-- Employees List -->
    <div class="employees-list" *ngIf="filteredEmployees.length > 0; else noEmployees">
      <div
        *ngFor="let row of pagedEmployees; trackBy: trackByEmployeeId"
        class="employee-row"
      >
        <div class="employee-main clickable" (click)="goToEmployeeDetail(row)">
          <div class="employee-top-row">
            <div class="employee-name-wrap">
              <div class="employee-name">
                {{ row.fullName }}
              </div>

              <div class="employee-badges">
                <span class="employee-number-badge">#{{ row.employeeNumber }}</span>
                <span class="company-badge" *ngIf="row.companyName">{{ row.companyName }}</span>
              </div>
            </div>
          </div>

          <div class="employee-meta">
            <span *ngIf="row.departmentCode || row.departmentName">
              {{ row.departmentCode }} {{ row.departmentName }}
            </span>
            <span *ngIf="row.vacationPolicyName">Vacation Policy: {{ row.vacationPolicyName }}</span>
            <span *ngIf="row.sickPolicyName">Sick Policy: {{ row.sickPolicyName }}</span>
          </div>
        </div>

        <div class="balances-grid">
          <div class="balance-card vacation">
            <div class="balance-label">Vacation</div>
            <div class="balance-value" [ngClass]="getBalanceClass(row.vacationAvailableHours)">
              {{ row.vacationAvailableHours }}
            </div>
            <div class="balance-subtext">Used: {{ row.vacationUsedHours }}</div>
          </div>

          <div class="balance-card sick">
            <div class="balance-label">Sick</div>
            <div class="balance-value" [ngClass]="getBalanceClass(row.sickAvailableHours)">
              {{ row.sickAvailableHours }}
            </div>
            <div class="balance-subtext">Used: {{ row.sickUsedHours }}</div>
          </div>
        </div>

        <div class="employee-actions">
          <button class="btn view" (click)="goToEmployeeDetail(row)">
            View
          </button>

          <button class="btn adjust" (click)="openManualAdjustmentModal(row)">
            Adjust
          </button>

          <button class="btn assign" (click)="openAssignPolicyModal(row)">
            Assign Policy
          </button>
        </div>
      </div>
    </div>

    <ng-template #noEmployees>
      <div class="empty-state">
        No employees match the current filters.
      </div>
    </ng-template>
  </ng-container>

  <!-- Manual Adjustment Modal -->
  <div *ngIf="manualAdjustmentModalOpen" class="modal-backdrop" (click)="closeManualAdjustmentModal()">
    <div class="modal" (click)="$event.stopPropagation()">
      <h2>Manual Adjustment</h2>

      <p class="modal-text" *ngIf="selectedEmployeeForAdjustment">
        Update balances for <strong>{{ selectedEmployeeForAdjustment.fullName }}</strong>.
      </p>

      <div class="modal-form">
        <label class="form-field">
          <span>Leave Type</span>
          <select [(ngModel)]="manualAdjustmentForm.leaveType">
            <option [ngValue]="null">Select leave type</option>
            <option [ngValue]="'Vacation'">Vacation</option>
            <option [ngValue]="'Sick'">Sick</option>
          </select>
        </label>

        <label class="form-field">
          <span>Adjustment Type</span>
          <select [(ngModel)]="manualAdjustmentForm.adjustmentMode">
            <option value="add">Add Hours</option>
            <option value="subtract">Subtract Hours</option>
          </select>
        </label>

        <label class="form-field">
          <span>Hours</span>
          <input
            type="number"
            min="0"
            step="0.01"
            [(ngModel)]="manualAdjustmentForm.hours"
            placeholder="0.00"
          />
        </label>

        <label class="form-field">
          <span>Note</span>
          <textarea
            [(ngModel)]="manualAdjustmentForm.note"
            placeholder="Enter reason for manual adjustment"
          ></textarea>
        </label>
      </div>

      <div class="modal-buttons">
        <button
          type="button"
          class="btn btn-secondary"
          (click)="closeManualAdjustmentModal()"
          [disabled]="isSavingManualAdjustment"
        >
          Cancel
        </button>

        <button
          type="button"
          class="btn btn-primary"
          (click)="saveManualAdjustment()"
          [disabled]="isSavingManualAdjustment"
        >
          {{ isSavingManualAdjustment ? 'Saving...' : 'Save Adjustment' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Assign Policy Modal -->
  <div *ngIf="assignPolicyModalOpen" class="modal-backdrop" (click)="closeAssignPolicyModal()">
    <div class="modal" (click)="$event.stopPropagation()">
      <h2>Assign Policy</h2>

      <p class="modal-text" *ngIf="selectedEmployeeForPolicyAssignment">
        Assign a leave policy to <strong>{{ selectedEmployeeForPolicyAssignment.fullName }}</strong>.
      </p>

      <div class="modal-form">
        <label class="form-field">
          <span>Leave Type</span>
          <select
            [ngModel]="assignPolicyForm.leaveType"
            (ngModelChange)="onAssignLeaveTypeChange($event)"
          >
            <option [ngValue]="null">Select leave type</option>
            <option [ngValue]="'Vacation'">Vacation</option>
            <option [ngValue]="'Sick'">Sick</option>
          </select>
        </label>

        <label class="form-field">
          <span>Policy</span>
          <select [(ngModel)]="assignPolicyForm.policyId">
            <option [ngValue]="null">Select policy</option>
            <option *ngFor="let policy of assignablePolicies" [ngValue]="policy.id">
              {{ policy.name }}
            </option>
          </select>
        </label>

        <label class="form-field">
          <span>Start Date</span>
          <input
            type="date"
            [(ngModel)]="assignPolicyForm.startDate"
          />
        </label>
      </div>

      <div class="modal-buttons">
        <button
          type="button"
          class="btn btn-secondary"
          (click)="closeAssignPolicyModal()"
          [disabled]="isAssigningPolicy"
        >
          Cancel
        </button>

        <button
          type="button"
          class="btn btn-primary"
          (click)="saveAssignedPolicy()"
          [disabled]="isAssigningPolicy"
        >
          {{ isAssigningPolicy ? 'Saving...' : 'Assign Policy' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Toast -->
  <div *ngIf="toastVisible" class="toast" [ngClass]="toastType">
    <img class="icon" alt="icon" />
    <span>{{ toastMessage }}</span>
  </div>
</div>